import { traducirError, useTraduccion } from '@tallerya/i18n';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Ayuda } from '../Ayuda';
import { IconoCheck, IconoChevronAbajo, IconoMas } from '../iconos';
import {
  acomodarDocumento,
  esCorreo,
  esTelefono,
  guiarDocumento,
  guiarNumero,
  guiarTelefono,
  mascaraValida,
  mostrarNumero,
  resolverNumero,
  soloDigitos,
  type FormaNumero,
} from './formatos';
import type { EstadoFormulario } from './useFormulario';
import { clave } from './valores';
import './formulario.css';

// Los campos del formulario. Cada uno se conecta solo al estado con `form` y
// `nombre`, y trae su etiqueta, su ayuda y su error: una maestra escribe
//
//   <CampoTexto form={form} nombre="name" etiqueta="Nombre" obligatorio ancho="largo" />
//
// y no arma ni un `<label>` ni un `<input>` por su cuenta.
//
// Medidas del Figma: etiqueta Bold 12, control de 35 px con radio 5, ayuda
// Regular 11 debajo. Con error, el borde y la ayuda pasan a rojo y **el error
// reemplaza a la ayuda**: dos líneas debajo del campo ya no se leen.

/** Los campos de `V` cuyo valor es de tipo `T`: a `CampoTexto` solo se le pueden dar textos. */
type ClavesDe<V, T> = { [K in keyof V]-?: V[K] extends T ? K : never }[keyof V] & string;

interface BaseCampo<V, T> {
  form: EstadoFormulario<V>;
  nombre: ClavesDe<V, T>;
  etiqueta: string;
  obligatorio?: boolean;
  /** Qué es el campo, en el globo del ⓘ junto a la etiqueta (#120). */
  info?: string;
  /** `corto` = 236 px, `largo` = 500 px (los dos anchos del Figma), `completo` = toda la fila. */
  ancho?: 'corto' | 'largo' | 'completo';
}

export interface OpcionCampo {
  valor: string;
  etiqueta: string;
}

/** La forma que tiene que tener el valor: el código del error, o nada si la cumple. */
type ReglaFormato = (valor: unknown) => string | undefined;

/**
 * Los caracteres que caben, como el tope de la API (#176). Al llegar al 90 %
 * aparece a la derecha, bajo el campo, cuántos quedan; pasado el tope, en rojo y en
 * negativo, con «Máximo N caracteres» a la izquierda, y el formulario no guarda.
 *
 * **No es un `maxLength`**: el navegador cortaría en silencio lo que se pega, y un
 * correo pegado de más quedaría guardado sin su última letra. Se cuenta recortado,
 * como la API.
 */
interface ConMaximo {
  maximo?: number | undefined;
}

/** Desde qué parte del tope se muestra el contador. */
const AVISO_DESDE = 0.9;

/** Lo que el contador dice: nada lejos del tope, lo que queda cerca, y lo que sobra al pasarse. */
function contadorDe(valor: unknown, maximo: number | undefined) {
  if (maximo === undefined) return null;
  const quedan = maximo - String(valor ?? '').trim().length;
  if (maximo - quedan < maximo * AVISO_DESDE) return null;
  return { quedan, excedido: quedan < 0, texto: quedan < 0 ? `−${String(-quedan)}` : String(quedan) };
}

/** El marco común de un campo: etiqueta, control y ayuda o error. */
function Campo<V>({
  form,
  nombre,
  etiqueta,
  obligatorio,
  info,
  ancho = 'corto',
  formato,
  maximo,
  children,
}: BaseCampo<V, unknown> &
  ConMaximo & { formato?: ReglaFormato | undefined; children: (aria: AriaControl) => ReactNode }) {
  const { t } = useTraduccion();
  const id = form.idDe(nombre);
  const contador = contadorDe(leer(form, nombre), maximo);
  // Pasado el tope, el error se ve al momento, sin esperar a salir del campo. El
  // «Es demasiado largo» de la API —un dato viejo— también dice cuánto cabe.
  const crudo = form.errores[nombre];
  const error =
    maximo !== undefined && (contador?.excedido || crudo === traducirError('validation.too_long', t))
      ? t('ui.campo.maximo', { maximo })
      : crudo;
  const idError = `${id}-ayuda`;
  const idInfo = `${id}-info`;
  const describe = [error && idError, info && idInfo].filter(Boolean).join(' ');

  // El asterisco obliga: con el campo vacío, el formulario no guarda. Ninguna
  // pantalla tiene que repetirlo en su `validar`.
  const { registrarObligatorio, registrarFormato, revisarFormato } = form;
  useEffect(
    () => (obligatorio ? registrarObligatorio(nombre) : undefined),
    [registrarObligatorio, nombre, obligatorio],
  );

  // La forma, igual: el campo la registra y el formulario no guarda sin ella
  // (#155). Por un ref, porque la regla cambia con lo que la pantalla le pase —la
  // máscara del tipo de documento elegido— y volver a registrarla en cada dibujado
  // no hace falta: el formulario la llama cuando la necesita.
  //
  // El tope va por el mismo camino: un valor que ya pasaba no se recorta solo.
  const regla = useRef(formato);
  useEffect(() => {
    regla.current = formato;
  });
  const conFormato = formato !== undefined || maximo !== undefined;
  useEffect(
    () =>
      conFormato
        ? registrarFormato(nombre, (valor) =>
            maximo !== undefined && String(valor).trim().length > maximo ? 'validation.too_long' : regla.current?.(valor),
          )
        : undefined,
    [registrarFormato, nombre, conFormato, maximo],
  );

  return (
    <div className={`ui-campo ui-campo--${ancho}${error ? ' ui-campo--error' : ''}`}>
      <div className="ui-campo__cabecera">
        <label htmlFor={id} className="ui-campo__etiqueta">
          {etiqueta}
          {obligatorio && <span aria-hidden="true"> *</span>}
        </label>
        {info && <Ayuda tema={etiqueta} texto={info} />}
      </div>
      {/* Oculto, pero el lector lo lee al enfocar el campo, como leía la ayuda de abajo. */}
      {info && (
        <span id={idInfo} hidden>
          {info}
        </span>
      )}
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describe || undefined,
        'aria-required': obligatorio || undefined,
        onBlur: conFormato ? () => revisarFormato(nombre) : undefined,
      })}
      {/* Debajo del campo, solo el error y, con tope, el contador en la misma línea:
          la explicación va tras el ⓘ (#121, #176). */}
      {(error || contador) && (
        <div className="ui-campo__pie">
          {error && (
            <p id={idError} className="ui-campo__ayuda">
              {error}
            </p>
          )}
          {contador && (
            <span
              className={`ui-campo__contador${contador.excedido ? ' ui-campo__contador--excedido' : ''}`}
              aria-hidden="true"
            >
              {contador.texto}
            </span>
          )}
        </div>
      )}
      {/* El lector no lee el número a cada tecla: avisa al acercarse y al pasarse. */}
      {maximo !== undefined && (
        <span className="ui-solo-lectores" aria-live="polite">
          {contador &&
            (contador.excedido
              ? t('ui.campo.excedido', { maximo })
              : t('ui.campo.cerca', { maximo }))}
        </span>
      )}
    </div>
  );
}

interface AriaControl {
  id: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
  'aria-required': true | undefined;
  /** Solo en un campo con forma: la revisa al salir de él. */
  onBlur: (() => void) | undefined;
}

// Leer y escribir un campo de un `V` genérico: TypeScript no puede seguir que
// `nombre` es una clave de texto a través del genérico, así que la conversión
// se hace en un solo lugar y no en cada campo.
function leer<V>(form: EstadoFormulario<V>, nombre: keyof V): unknown {
  return form.valores[nombre];
}
function escribir<V>(form: EstadoFormulario<V>, nombre: keyof V, valor: unknown) {
  form.cambiar(nombre, valor as V[keyof V]);
}

/** El teclado que abre el celular. */
type Teclado = 'text' | 'decimal' | 'numeric' | 'tel' | 'email';

interface PropsTexto<V> extends BaseCampo<V, string>, ConMaximo {
  /**
   * `datetime-local`: fecha y hora en la de quien escribe, como la entrega prometida.
   * Sin `number` a propósito: un número va en `CampoNumero` (#156).
   */
  tipo?: 'text' | 'email' | 'tel' | 'date' | 'datetime-local';
  placeholder?: string | undefined;
  prefijo?: string;
  sufijo?: string;
  /** Sin los teclados numéricos, por lo mismo que `tipo`. */
  inputMode?: Exclude<Teclado, 'decimal' | 'numeric'>;
  autoComplete?: string;
}

/** Texto de una línea. `prefijo` y `sufijo` van dentro de la caja, en gris: «$», «horas». */
export function CampoTexto<V>(props: PropsTexto<V>) {
  return <CajaDeTexto {...props} />;
}

/**
 * `CampoTexto` con lo que agregan los campos con forma: `transformar` decide qué
 * queda escrito a cada tecla —recibe lo que había y lo que llegó—, `mostrar`, cómo
 * se ve el valor en la caja si no es tal cual —el número con sus comas de miles—, y
 * `formato` es la regla que el formulario revisa al salir del campo y al guardar.
 */
function CajaDeTexto<V>({
  tipo = 'text',
  placeholder,
  prefijo,
  sufijo,
  inputMode,
  autoComplete,
  transformar,
  mostrar = (v) => v,
  alSalir,
  ...base
}: Omit<PropsTexto<V>, 'inputMode'> & {
  inputMode?: Teclado;
  /** `pegado`: lo que llegó vino de pegar o arrastrar, no de una tecla. */
  transformar?: (anterior: string, nuevo: string, pegado: boolean) => string;
  /** Recibe el valor y devuelve lo que se ve; `transformar` recibe lo que se ve. */
  mostrar?: (valor: string) => string;
  /** Al salir del campo, antes de revisar su forma. */
  alSalir?: (() => void) | undefined;
  formato?: ReglaFormato | undefined;
}) {
  const { form, nombre } = base;
  const valor = String(leer(form, nombre) ?? '');
  return (
    <Campo {...base}>
      {(aria) => (
        <div className="ui-campo__caja">
          {prefijo && <span className="ui-campo__adorno">{prefijo}</span>}
          <input
            {...aria}
            type={tipo}
            value={mostrar(valor)}
            placeholder={placeholder}
            inputMode={inputMode}
            autoComplete={autoComplete}
            onBlur={
              alSalir
                ? () => {
                    alSalir();
                    aria.onBlur?.();
                  }
                : aria.onBlur
            }
            onChange={(e) => {
              const caja = e.target;
              const escrito = caja.value;
              const evento = e.nativeEvent;
              const pegado = evento instanceof InputEvent && evento.inputType.startsWith('insertFrom');
              const atras = evento instanceof InputEvent && evento.inputType === 'deleteContentBackward';
              const nuevo = transformar ? transformar(valor, escrito, pegado) : escrito;
              const mostrado = mostrar(nuevo);
              // Una tecla que no entra no cambia nada: ni el valor ni el error.
              if (nuevo !== valor) escribir(form, nombre, nuevo);
              // Si `transformar` cambió lo escrito, React le pone a la caja su valor y el
              // navegador manda el cursor al final: con «1234» y el cursor en medio, una
              // letra rechazada dejaba el siguiente dígito al final. Vuelve a donde iba.
              // Después de React, que repone el valor al terminar el evento.
              if (mostrado !== escrito) {
                const fin = caja.selectionEnd ?? escrito.length;
                const cursor = cursorTras(escrito, fin, mostrado, nuevo === valor, atras);
                queueMicrotask(() => {
                  if (document.activeElement === caja) caja.setSelectionRange(cursor, cursor);
                });
              }
            }}
          />
          {sufijo && <span className="ui-campo__adorno">{sufijo}</span>}
        </div>
      )}
    </Campo>
  );
}

const SIGNIFICATIVO = /[\p{L}\p{N}]/u;

/**
 * Dónde va el cursor cuando `transformar` cambió lo escrito. Si lo rechazó, donde
 * estaba antes de la tecla; un Retroceso rechazado —sobre la coma de miles o el
 * guion, que se ponen solos— lo deja delante de ese signo, y el siguiente borra el
 * dígito. Si lo acomodó, tras los mismos dígitos y letras que tenía delante: el
 * guion que la máscara pone o quita (#155) y las comas de miles (#156) no lo
 * corren. Si lo último tecleado fue un separador —el punto—, también tras él.
 */
function cursorTras(escrito: string, fin: number, nuevo: string, rechazado: boolean, atras: boolean): number {
  if (rechazado && atras && escrito.length < nuevo.length) return fin;
  if (rechazado) return Math.min(Math.max(fin + nuevo.length - escrito.length, 0), nuevo.length);
  let delante = [...escrito.slice(0, fin)].filter((c) => SIGNIFICATIVO.test(c)).length;
  let i = 0;
  while (i < nuevo.length && delante > 0) if (SIGNIFICATIVO.test(nuevo[i++] ?? '')) delante--;
  const separador = fin > 0 && !SIGNIFICATIVO.test(escrito[fin - 1] ?? '');
  if (separador && i < nuevo.length && !SIGNIFICATIVO.test(nuevo[i] ?? '')) i++;
  return i;
}

/**
 * Un número: un precio, una cantidad, unas horas, un año (#156). No deja escribir
 * ni pegar una letra, acepta un solo punto decimal y no pasa de `decimales`, que
 * con `0` es un entero, ni de `enteros` dígitos delante del punto. La coma de
 * miles la pone solo —`1,500.00`— salvo con `sinMiles`, y no va en el valor:
 * el formulario guarda `1500.00`. El `-`, solo con `negativos`.
 *
 * Una coma tecleada se lee al salir del campo o al guardar, por lo que tiene
 * detrás (`resolverNumero`): `1,500` es mil quinientos y `45,50`, cuarenta y
 * cinco con cincuenta. Si no se sabe —`1,5000`—, queda marcada y no guarda.
 *
 * No es un `type="number"`: Firefox le deja escribir letras, y la rueda del mouse
 * le cambia el valor a quien solo quería bajar por la pantalla. El valor sigue
 * siendo texto, como en los demás campos; leerlo como número y revisar sus rangos
 * es del `validar` de cada pantalla.
 */
export function CampoNumero<V>({
  decimales,
  enteros,
  negativos,
  sinMiles,
  ...props
}: BaseCampo<V, string> &
  FormaNumero & { placeholder?: string; prefijo?: string; sufijo?: string }) {
  const forma = { decimales, enteros, negativos, sinMiles };
  const { form, nombre } = props;

  // Quien guarda con Enter no sale del campo: la coma se lee igual antes de validar.
  const { registrarPendiente } = form;
  useEffect(
    () =>
      registrarPendiente(nombre, (actual) =>
        comaLeida({ decimales, enteros, negativos, sinMiles }, String(actual ?? '')),
      ),
    [registrarPendiente, nombre, decimales, enteros, negativos, sinMiles],
  );

  return (
    <CajaDeTexto
      {...props}
      autoComplete="off"
      // El teclado numérico del celular no trae el `-`: con negativos, el de texto.
      inputMode={negativos ? 'text' : decimales === 0 ? 'numeric' : 'decimal'}
      transformar={(anterior, nuevo, pegado) => guiarNumero(forma, anterior, nuevo, pegado)}
      mostrar={(valor) => mostrarNumero(forma, valor)}
      alSalir={() => {
        const leida = comaLeida(forma, String(leer(form, nombre) ?? ''));
        if (leida !== undefined) escribir(form, nombre, leida);
      }}
      // Lo que queda con coma es lo que `resolverNumero` no supo leer.
      formato={(valor) => (String(valor).includes(',') ? 'validation.numero_coma' : undefined)}
    />
  );
}

/** El valor con su coma tecleada ya leída, o nada si no tiene o no se puede leer. */
function comaLeida(forma: FormaNumero, valor: string): string | undefined {
  if (!valor.includes(',')) return undefined;
  return resolverNumero(forma, valor) ?? undefined;
}

/**
 * Un teléfono (#155): solo dígitos, espacios y `+ ( ) -`, con al menos un dígito.
 * Lo demás no entra al escribir, y sin dígitos no se guarda. Nada sobre el código de
 * país: es la regla de la API (`ContactFormats`). El largo, con `maximo`.
 */
export function CampoTelefono<V>(props: BaseCampo<V, string> & ConMaximo & { placeholder?: string }) {
  return (
    <CajaDeTexto
      {...props}
      tipo="tel"
      inputMode="tel"
      autoComplete="tel"
      transformar={guiarTelefono}
      formato={(valor) => (esTelefono(String(valor)) ? undefined : 'validation.phone')}
    />
  );
}

/** Un correo (#155): `algo@dominio.tld`, sin espacios. Se revisa al salir del campo. */
export function CampoCorreo<V>(props: BaseCampo<V, string> & ConMaximo & { placeholder?: string }) {
  return (
    <CajaDeTexto
      {...props}
      tipo="email"
      inputMode="email"
      autoComplete="email"
      formato={(valor) => (esCorreo(String(valor)) ? undefined : 'validation.email')}
    />
  );
}

/**
 * El número de un documento, guiado por la máscara de su tipo (#155): el DUI
 * pone su guion mientras se escribe y no deja meter una letra. `mascara` viene de
 * la API con el tipo elegido; sin ella —el pasaporte— se escribe libre, y también
 * si llega una que no se entiende.
 *
 * Quien ordena y valida de verdad es la API: esto guía, y al guardar dice lo mismo
 * que ella diría.
 */
export function CampoDocumento<V>({
  mascara,
  ...base
}: BaseCampo<V, string> & ConMaximo & { mascara?: string | null | undefined }) {
  // Solo una máscara que se entiende guía y revisa; otra, se escribe libre.
  const valida = mascara && mascaraValida(mascara) ? mascara : null;
  return (
    <CajaDeTexto
      {...base}
      autoComplete="off"
      inputMode={soloDigitos(mascara) ? 'numeric' : 'text'}
      // La forma de ejemplo, si es una sola: «00000000-0». Con varias, ninguna
      // sería la verdad entera.
      placeholder={valida && !valida.includes('|') ? valida : undefined}
      transformar={(anterior, nuevo) => guiarDocumento(mascara, anterior, nuevo)}
      formato={
        valida
          ? (valor) => (acomodarDocumento(valida, String(valor)) === null ? 'document.numero_no_calza' : undefined)
          : undefined
      }
    />
  );
}

/** Texto de varias líneas: notas, dirección. */
export function CampoAreaTexto<V>({
  placeholder,
  filas = 3,
  ...base
}: BaseCampo<V, string> & ConMaximo & { placeholder?: string; filas?: number }) {
  const { form, nombre } = base;
  return (
    <Campo {...base}>
      {(aria) => (
        <textarea
          {...aria}
          className="ui-campo__caja ui-campo__area"
          rows={filas}
          value={String(leer(form, nombre) ?? '')}
          placeholder={placeholder}
          onChange={(e) => escribir(form, nombre, e.target.value)}
        />
      )}
    </Campo>
  );
}

/** Lo que se reserva para decidir si la lista cabe abajo de la caja. */
const ALTO_LISTA = 260;
/** El ancho mínimo de la lista: la del Figma es más ancha que una caja corta. */
const ANCHO_MINIMO_LISTA = 200;

/**
 * Una de varias opciones, en una lista que se despliega: la «Categoría» del Figma.
 *
 * **No es un `<select>`**: la lista de uno nativo la dibuja el navegador y no
 * lleva el diseño —el check y el fondo rojo de la elegida, el divisor, la acción
 * del pie—. Por eso el teclado y el lector de pantalla van escritos a mano, con
 * el patrón *select-only combobox* de WAI-ARIA: el foco se queda en el botón, y
 * la opción resaltada se anuncia con `aria-activedescendant`. Flechas, Inicio y
 * Fin la mueven, Enter o Espacio la eligen, Escape cierra, y una letra salta a la
 * siguiente que empieza con ella, como en el nativo.
 *
 * La lista va en posición fija, como `Menu`: dentro de una `Ventana` o de un
 * marco con desplazamiento no queda recortada. Por eso se cierra al desplazar.
 *
 * `accion` es lo del pie, tras un divisor: «+ Agregar categoría». No es un valor:
 * elegirla no cambia el campo, solo avisa.
 */
export function CampoSeleccion<V>({
  opciones,
  placeholder,
  accion,
  ...base
}: BaseCampo<V, string> & {
  opciones: readonly OpcionCampo[];
  placeholder?: string;
  accion?: { etiqueta: string; onSeleccionar: () => void };
}) {
  const { form, nombre } = base;
  const valor = String(leer(form, nombre) ?? '');
  // Sin obligatorio se puede volver a «ninguna»: el placeholder es la primera
  // opción. Con obligatorio solo se ve en la caja, hasta que se elige algo.
  const lista: readonly OpcionCampo[] =
    placeholder && !base.obligatorio ? [{ valor: '', etiqueta: placeholder }, ...opciones] : opciones;
  const elegida = opciones.find((o) => o.valor === valor);
  const total = lista.length + (accion ? 1 : 0);

  const [posicion, setPosicion] = useState<CSSProperties | null>(null);
  const abierta = posicion !== null;
  // La resaltada: un índice de `lista`, o `lista.length` para la acción.
  const [activa, setActiva] = useState(0);
  const raiz = useRef<HTMLDivElement>(null);
  const idLista = useId();
  const idOpcion = (i: number) => `${idLista}-${String(i)}`;

  function abrir() {
    const caja = raiz.current?.getBoundingClientRect();
    if (!caja) return;
    const ancho = Math.max(caja.width, ANCHO_MINIMO_LISTA);
    const left = Math.max(8, Math.min(caja.left, window.innerWidth - 8 - ancho));
    // Hacia arriba solo si abajo no cabe y arriba sí.
    const abajo = window.innerHeight - caja.bottom >= ALTO_LISTA || caja.top < ALTO_LISTA;
    setPosicion(
      abajo
        ? { top: caja.bottom + 4, left, minWidth: ancho }
        : { bottom: window.innerHeight - caja.top + 4, left, minWidth: ancho },
    );
    setActiva(Math.max(0, lista.findIndex((o) => o.valor === valor)));
  }

  const cerrar = () => setPosicion(null);

  function elegir(i: number) {
    cerrar();
    if (i === lista.length) {
      accion?.onSeleccionar();
      return;
    }
    const opcion = lista[i];
    if (opcion && opcion.valor !== valor) escribir(form, nombre, opcion.valor);
  }

  /** La siguiente que empieza con esa letra, sin tildes ni mayúsculas, dando la vuelta. */
  function porLetra(letra: string): number | null {
    const desde = abierta ? activa : lista.findIndex((o) => o.valor === valor);
    for (let paso = 1; paso <= lista.length; paso++) {
      const i = (desde + paso + lista.length) % lista.length;
      if (clave(lista[i]?.etiqueta ?? '').startsWith(clave(letra))) return i;
    }
    return null;
  }

  function alTeclear(evento: KeyboardEvent<HTMLButtonElement>) {
    const { key } = evento;
    // Una letra suelta: cerrada, cambia el valor como el nativo; abierta, resalta.
    if (key.length === 1 && key !== ' ' && !evento.ctrlKey && !evento.metaKey && !evento.altKey) {
      const i = porLetra(key);
      if (i === null) return;
      if (abierta) setActiva(i);
      else escribir(form, nombre, lista[i]?.valor ?? '');
      return;
    }

    if (!abierta) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        evento.preventDefault();
        abrir();
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        evento.preventDefault();
        setActiva((a) => Math.min(a + 1, total - 1));
        break;
      case 'ArrowUp':
        evento.preventDefault();
        setActiva((a) => Math.max(a - 1, 0));
        break;
      case 'Home':
        evento.preventDefault();
        setActiva(0);
        break;
      case 'End':
        evento.preventDefault();
        setActiva(total - 1);
        break;
      case 'Enter':
      case ' ':
        evento.preventDefault();
        elegir(activa);
        break;
      case 'Escape':
        // Que el Escape cierre la lista y no llegue a la `Ventana` de atrás.
        evento.preventDefault();
        evento.stopPropagation();
        cerrar();
        break;
      case 'Tab':
        cerrar();
        break;
    }
  }

  useEffect(() => {
    if (!abierta) return;

    function alTocar(evento: PointerEvent) {
      if (!raiz.current?.contains(evento.target as Node)) cerrar();
    }
    // La lista se desplaza sola si es larga: eso no la cierra. Lo demás sí, o se
    // quedaría flotando lejos de su caja.
    function alDesplazar(evento: Event) {
      if (!document.getElementById(idLista)?.contains(evento.target as Node)) cerrar();
    }

    document.addEventListener('pointerdown', alTocar);
    window.addEventListener('scroll', alDesplazar, { capture: true });
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('pointerdown', alTocar);
      window.removeEventListener('scroll', alDesplazar, { capture: true });
      window.removeEventListener('resize', cerrar);
    };
  }, [abierta, idLista]);

  // La resaltada siempre a la vista, también al bajar con las flechas en una lista larga.
  useEffect(() => {
    if (abierta) document.getElementById(`${idLista}-${String(activa)}`)?.scrollIntoView({ block: 'nearest' });
  }, [abierta, activa, idLista]);

  /** Una opción: el clic no le quita el foco al botón, y el mouse resalta como las flechas. */
  const comoOpcion = (i: number) => ({
    id: idOpcion(i),
    role: 'option' as const,
    onMouseDown: (e: MouseEvent) => e.preventDefault(),
    onMouseEnter: () => setActiva(i),
    onClick: () => elegir(i),
  });

  return (
    <Campo {...base}>
      {(aria) => (
        <div className="ui-seleccion" ref={raiz}>
          <div className={`ui-campo__caja${elegida ? '' : ' ui-campo__caja--vacia'}`}>
            <button
              {...aria}
              type="button"
              role="combobox"
              className="ui-seleccion__boton"
              aria-haspopup="listbox"
              aria-expanded={abierta}
              aria-controls={abierta ? idLista : undefined}
              aria-activedescendant={abierta ? idOpcion(activa) : undefined}
              onClick={() => (abierta ? cerrar() : abrir())}
              onKeyDown={alTeclear}
              // Espacio activa un botón al soltarlo: sin esto, el que eligió una
              // opción la volvería a abrir.
              onKeyUp={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
            >
              <span className="ui-seleccion__texto">{elegida?.etiqueta ?? placeholder ?? ''}</span>
              <IconoChevronAbajo />
            </button>
          </div>

          {posicion && (
            <div id={idLista} role="listbox" className="ui-seleccion__lista" style={posicion}>
              {lista.map((o, i) => {
                const marcada = o.valor === valor;
                const clases = [
                  'ui-seleccion__opcion',
                  marcada && 'ui-seleccion__opcion--elegida',
                  i === activa && 'ui-seleccion__opcion--activa',
                  o.valor === '' && 'ui-seleccion__opcion--ninguna',
                ];
                return (
                  <div key={o.valor || '__ninguna'} {...comoOpcion(i)} aria-selected={marcada} className={clases.filter(Boolean).join(' ')}>
                    {marcada && <IconoCheck />}
                    <span>{o.etiqueta}</span>
                  </div>
                );
              })}
              {accion && (
                <>
                  <div className="ui-seleccion__divisor" aria-hidden="true" />
                  <div
                    {...comoOpcion(lista.length)}
                    aria-selected={false}
                    className={`ui-seleccion__opcion ui-seleccion__accion${activa === lista.length ? ' ui-seleccion__opcion--activa' : ''}`}
                  >
                    <IconoMas />
                    <span>{accion.etiqueta}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Campo>
  );
}

/**
 * Pocas opciones que se excluyen, a la vista: «Servicio | Producto».
 *
 * Por debajo son radios nativos: las flechas del teclado cambian de opción y el
 * lector de pantalla dice «1 de 2», sin escribir nada de eso a mano.
 */
export function CampoSegmentado<V>({
  opciones,
  ...base
}: BaseCampo<V, string> & { opciones: readonly OpcionCampo[] }) {
  const { form, nombre } = base;
  const valor = String(leer(form, nombre) ?? '');
  return (
    <Campo {...base}>
      {({ id, ...aria }) => (
        <div role="radiogroup" id={id} tabIndex={-1} className="ui-segmentado" {...aria}>
          {opciones.map((o) => (
            <label key={o.valor} className="ui-segmentado__opcion">
              <input
                type="radio"
                name={id}
                value={o.valor}
                checked={valor === o.valor}
                onChange={() => escribir(form, nombre, o.valor)}
              />
              <span>{o.etiqueta}</span>
            </label>
          ))}
        </div>
      )}
    </Campo>
  );
}

/**
 * Varias opciones que se marcan a la vez, a la vista: las sucursales de un
 * mecánico (#58). El valor es la lista de los `valor` marcados.
 *
 * Casillas nativas, por lo mismo que el segmentado usa radios: el teclado y el
 * lector de pantalla ya saben qué hacer con ellas. Para pocas opciones —las
 * sucursales de un taller mediano—; con muchas, un combo con búsqueda.
 */
export function CampoCasillas<V>({
  opciones,
  ...base
}: BaseCampo<V, string[]> & { opciones: readonly OpcionCampo[] }) {
  const { form, nombre } = base;
  const marcadas = (leer(form, nombre) as string[] | undefined) ?? [];
  return (
    <Campo {...base}>
      {({ id, ...aria }) => (
        <div role="group" id={id} tabIndex={-1} className="ui-casillas" {...aria}>
          {opciones.map((o) => (
            <label key={o.valor} className="ui-casillas__opcion">
              <input
                type="checkbox"
                value={o.valor}
                checked={marcadas.includes(o.valor)}
                onChange={(e) =>
                  escribir(
                    form,
                    nombre,
                    e.target.checked ? [...marcadas, o.valor] : marcadas.filter((m) => m !== o.valor),
                  )
                }
              />
              <span>{o.etiqueta}</span>
            </label>
          ))}
        </div>
      )}
    </Campo>
  );
}

/**
 * El interruptor de «Activo»: encendido o apagado, con su explicación al lado.
 * Es un checkbox con `role="switch"`, que el lector de pantalla anuncia como
 * «activado / desactivado».
 */
export function CampoInterruptor<V>({
  form,
  nombre,
  etiqueta,
  info,
}: {
  form: EstadoFormulario<V>;
  nombre: ClavesDe<V, boolean>;
  etiqueta: string;
  /** «Disponible para agregarse a órdenes. Apágalo cuando ya no se ofrezca.», en el globo del ⓘ (#121). */
  info?: string;
}) {
  const id = form.idDe(nombre);
  const error = form.errores[nombre];
  const idInfo = `${id}-info`;
  const describe = [error && `${id}-error`, info && idInfo].filter(Boolean).join(' ') || undefined;
  return (
    <div className="ui-interruptor-campo">
      <label className="ui-interruptor" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={Boolean(leer(form, nombre))}
          aria-invalid={error ? true : undefined}
          aria-describedby={describe}
          onChange={(e) => escribir(form, nombre, e.target.checked)}
        />
        <span className="ui-interruptor__pista" aria-hidden="true" />
        <span className="ui-interruptor__texto">
          <strong>{etiqueta}</strong>
          {error && (
            <span id={`${id}-error`} className="ui-interruptor__error">
              {error}
            </span>
          )}
        </span>
      </label>
      {info && (
        <>
          <Ayuda tema={etiqueta} texto={info} />
          {/* Oculto, pero el lector lo lee al enfocar el interruptor. */}
          <span id={idInfo} hidden>
            {info}
          </span>
        </>
      )}
    </div>
  );
}

/**
 * Una lista de palabras cortas que se escriben de a una: los «Otros nombres» de
 * un servicio —«balatas», «pastillas»—, y mañana las especialidades de un
 * mecánico. Cada una se agrega con Enter y queda como una pastilla, dentro de la
 * caja, con su «×» para quitarla.
 *
 * El valor es la lista entera. La normalización que importa —sin tildes, sin
 * mayúsculas— la hace la API al guardar (`CatalogAlias.Normalize`); acá solo se
 * evita lo obvio, para que la persona no vea dos pastillas iguales.
 */
export function CampoEtiquetas<V>({
  placeholder,
  etiquetaQuitar,
  sugerencias,
  ...base
}: BaseCampo<V, string[]> & {
  placeholder?: string;
  /** Lo que el lector de pantalla dice del «×» de cada pastilla, ya traducido: «Quitar». */
  etiquetaQuitar: string;
  /**
   * Las que ya existen, para que el navegador las ofrezca al escribir: así nadie
   * crea «Frenos» estando «frenos» (las especialidades, #58). Escribir una nueva
   * sigue valiendo.
   */
  sugerencias?: readonly string[];
}) {
  const { form, nombre } = base;
  const etiquetas = (leer(form, nombre) as string[] | undefined) ?? [];
  const [texto, setTexto] = useState('');
  // Lo escrito, también en un ref: el formulario lo pide al guardar, desde un
  // evento y no al dibujar, y tiene que ver la última tecla.
  const escrito = useRef('');

  const poner = (lista: string[]) => escribir(form, nombre, lista);

  function escribirTexto(valor: string) {
    escrito.current = valor;
    setTexto(valor);
  }

  // Lo que quedó sin Enter también se guarda: el formulario se lo pide a este
  // campo al enviar (`registrarPendiente`), sin depender del `blur` ni de que
  // la pantalla no se mueva bajo el dedo.
  const { registrarPendiente } = form;
  useEffect(
    () =>
      registrarPendiente(nombre, (actual) => {
        const nueva = escrito.current.trim();
        if (nueva === '') return undefined;
        escribirTexto('');
        return con((actual as string[] | undefined) ?? [], nueva);
      }),
    [registrarPendiente, nombre],
  );

  function alTeclear(evento: KeyboardEvent<HTMLInputElement>) {
    // Mientras se compone una palabra —el teclado con acentos, el autocorrector
    // de Android—, Enter confirma la composición, no la etiqueta.
    if (evento.key === 'Enter' && !evento.nativeEvent.isComposing) {
      // Enter adentro de un formulario lo mandaría: acá solo agrega.
      evento.preventDefault();
      const nueva = texto.trim();
      if (nueva === '') return;
      poner(con(etiquetas, nueva));
      escribirTexto('');
      return;
    }

    // Borrar con la caja vacía quita la última, como en cualquier campo de este
    // tipo. Solo vacía: con texto, Backspace borra una letra y nada más.
    if (evento.key === 'Backspace' && texto === '' && etiquetas.length > 0) {
      poner(etiquetas.slice(0, -1));
    }
  }

  // Como en el Figma, las pastillas van dentro de la caja, antes de lo que se
  // escribe. Tocar la caja fuera de una pastilla lleva al texto.
  const entrada = useRef<HTMLInputElement>(null);

  return (
    <Campo {...base}>
      {(aria) => (
        <div
          className="ui-campo__caja ui-etiquetas"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('button, input')) return;
            e.preventDefault();
            entrada.current?.focus();
          }}
        >
          {etiquetas.length > 0 && (
            <ul className="ui-etiquetas__lista" role="list">
              {etiquetas.map((e) => (
                <li key={e} className="ui-etiquetas__pastilla">
                  <span>{e}</span>
                  <button
                    type="button"
                    className="ui-etiquetas__quitar"
                    aria-label={`${etiquetaQuitar} ${e}`}
                    onClick={() => poner(etiquetas.filter((otra) => otra !== e))}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            {...aria}
            ref={entrada}
            type="text"
            value={texto}
            placeholder={placeholder}
            autoComplete="off"
            {...(sugerencias && { list: `${aria.id}-sugerencias` })}
            onChange={(e) => {
              // Elegir una sugerencia de la lista ya es decidirla: entra como
              // pastilla sin pedir Enter. Chrome lo marca como reemplazo, y
              // Firefox lo avisa con un evento que no es de teclado.
              const valor = e.target.value;
              const nativo = e.nativeEvent;
              const elegida =
                (!(nativo instanceof InputEvent) || nativo.inputType === 'insertReplacementText') &&
                sugerencias?.includes(valor);
              if (elegida) {
                poner(con(etiquetas, valor));
                escribirTexto('');
                return;
              }
              escribirTexto(valor);
            }}
            onKeyDown={alTeclear}
          />
          {/* Las que ya están puestas no se vuelven a ofrecer. */}
          {sugerencias && (
            <datalist id={`${aria.id}-sugerencias`}>
              {sugerencias
                .filter((s) => !etiquetas.some((e) => clave(e) === clave(s)))
                .map((s) => (
                  <option key={s} value={s} />
                ))}
            </datalist>
          )}
        </div>
      )}
    </Campo>
  );
}

/**
 * La lista con una etiqueta más. «Balatas» y «balatas» son la misma: se compara
 * como la API, sin mayúsculas ni tildes, y la que ya estaba se queda.
 */
function con(etiquetas: string[], nueva: string): string[] {
  return etiquetas.some((e) => clave(e) === clave(nueva)) ? etiquetas : [...etiquetas, nueva];
}
